import { useAuth } from '../contexts/AuthContext';
import { getUserToday, getUserDateOffset, getDateRange, formatDateForUser, getDayOfWeek, isToday } from '../utils/timezoneHelpers';

/**
 * Hook to access timezone-aware date utilities
 *
 * Provides helper functions that automatically use the authenticated user's timezone
 * Falls back to 'America/New_York' if user timezone not set
 *
 * @example
 * const { getToday, getYesterday, getTomorrow } = useUserTimezone();
 * const today = getToday(); // "2025-10-05" in user's timezone
 */
export function useUserTimezone() {
  const { user } = useAuth();

  // Get user's timezone or fallback to Eastern
  const timezone = user?.timezone || 'America/New_York';

  return {
    /** User's IANA timezone string */
    timezone,

    /** Get today's date in user's timezone (YYYY-MM-DD) */
    getToday: () => getUserToday(timezone),

    /** Get yesterday's date in user's timezone */
    getYesterday: () => getUserDateOffset(timezone, -1),

    /** Get tomorrow's date in user's timezone */
    getTomorrow: () => getUserDateOffset(timezone, 1),

    /** Get a date offset by N days from today */
    getDateOffset: (days: number) => getUserDateOffset(timezone, days),

    /** Get date range (e.g., last 7 days, last 30 days) */
    getRange: (range: '7days' | '30days' | 'month' | 'year') => getDateRange(range, timezone),

    /** Format a date string for display in user's timezone */
    formatDate: (dateString: string) => formatDateForUser(dateString, timezone),

    /** Get day of week for a date */
    getDayOfWeek: (dateString: string) => getDayOfWeek(dateString, timezone),

    /** Check if a date is today in user's timezone */
    isToday: (dateString: string) => isToday(dateString, timezone),
  };
}
